import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Search, Target, Star, X, Eye } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PanoramaViewer = () => {
  const { panoramaId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewUserId = searchParams.get('viewUserId');
  const [panorama, setPanorama] = useState(null);
  const [loading, setLoading] = useState(true);
  const viewerRef = useRef(null);
  const [hotspots, setHotspots] = useState([]);
  const [foundHotspotIds, setFoundHotspotIds] = useState([]);
  const [hintDialog, setHintDialog] = useState({ open: false, hotspot: null });
  const [highlightedHotspotId, setHighlightedHotspotId] = useState(null);
  const [userPointsBalance, setUserPointsBalance] = useState(0);
  const [allGameHotspots, setAllGameHotspots] = useState([]); // All hotspots in the game
  const [allFoundHotspotIds, setAllFoundHotspotIds] = useState([]); // All found hotspots in the game
  const [showHotspotsList, setShowHotspotsList] = useState(true);
  const [gameMode, setGameMode] = useState(null);
  const [hotspotMessage, setHotspotMessage] = useState(null);
  const [flyingStars, setFlyingStars] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const progressRef = useRef(null);
  const lastMousePos = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const { t, language } = useLanguage();
  const { user, getAuthHeader } = useAuth();
  
  const getTranslatedText = (translations, lang, fallback) => {
    if (translations && translations[lang]) return translations[lang];
    return fallback || '';
  };

  useEffect(() => {
    fetchPanorama();
  }, [panoramaId]);
  
  // Load hotspots when panorama is loaded
  useEffect(() => {
    if (panorama) {
      loadHotspots();
    }
  }, [panorama, user]);
  
  const loadHotspots = async () => {
    try {
      const hotspotsResp = await axios.get(`${API}/panoramas/${panoramaId}/hotspots`);
      setHotspots(hotspotsResp.data || []);
      
      // Fetch game mode if panorama belongs to a game
      if (panorama?.game_id) {
        try {
          const gameResp = await axios.get(`${API}/games/${panorama.game_id}`);
          const mode = gameResp.data?.game_mode;
          setGameMode(mode);
          // Auto-expand hotspots list for objects_only and location_objects modes
          if (true) { // Always expand hotspots list
            setShowHotspotsList(true);
          }
          
          // Load all hotspots for the entire game (for progress badge)
          const panoramasResp = await axios.get(`${API}/games/${panorama.game_id}/panoramas`);
          const allPanoramas = panoramasResp.data || [];
          
          // Fetch hotspots for all panoramas
          const allHotspotsPromises = allPanoramas.map(p => 
            axios.get(`${API}/panoramas/${p.id}/hotspots`).catch(() => ({ data: [] }))
          );
          const allHotspotsResults = await Promise.all(allHotspotsPromises);
          const allHs = allHotspotsResults.flatMap(r => r.data || []);
          setAllGameHotspots(allHs);
          
          // Fetch found hotspots for all panoramas (if logged in)
          if (user) {
            const allFoundPromises = allPanoramas.map(p =>
              axios.get(`${API}/panoramas/${p.id}/hotspots/found?_t=${Date.now()}`, { headers: getAuthHeader() })
                .catch(() => ({ data: { found_hotspot_ids: [] } }))
            );
            const allFoundResults = await Promise.all(allFoundPromises);
            const allFoundIds = allFoundResults.flatMap(r => r.data?.found_hotspot_ids || []);
            setAllFoundHotspotIds(allFoundIds);
          }
        } catch (e) {
        }
      }
      
      // Load user's points balance for hints
      if (user && !viewUserId) {
        try {
          const balanceResp = await axios.get(`${API}/users/me/points-balance`, { headers: getAuthHeader() });
          setUserPointsBalance(balanceResp.data.total_points || 0);
        } catch (e) {
          console.error('Error loading points balance:', e);
        }
      }
      
      // Load found hotspots - for viewUserId use that user's hotspots, otherwise current user
      if (viewUserId) {
        // Viewing another user's results - load their found hotspots
        try {
          const foundResp = await axios.get(
            `${API}/panoramas/${panoramaId}/hotspots/found/${viewUserId}?_t=${Date.now()}`
          );
          setFoundHotspotIds(foundResp.data.found_hotspot_ids || []);
        } catch (e) {
          setFoundHotspotIds([]);
        }
      } else if (user) {
        try {
          const foundResp = await axios.get(
            `${API}/panoramas/${panoramaId}/hotspots/found?_t=${Date.now()}`,
            { headers: getAuthHeader() }
          );
          setFoundHotspotIds(foundResp.data.found_hotspot_ids || []);
        } catch (e) {
          setFoundHotspotIds([]);
        }
      } else {
        setFoundHotspotIds([]);
      }
    } catch (e) {
    }
  };
  
  // Trigger flying stars animation
  const triggerFlyingStars = (points) => {
    const numStars = Math.min(points, 10);
    const newStars = [];
    
    let clickX = lastMousePos.current.x;
    let clickY = lastMousePos.current.y;
    
    if (viewerRef.current) {
      const rect = viewerRef.current.getBoundingClientRect();
      if (clickX === window.innerWidth / 2 && clickY === window.innerHeight / 2) {
        clickX = rect.left + rect.width / 2;
        clickY = rect.top + rect.height / 2;
      }
    }
    
    for (let i = 0; i < numStars; i++) {
      newStars.push({
        id: Date.now() + i,
        startX: clickX + (Math.random() - 0.5) * 60,
        startY: clickY + (Math.random() - 0.5) * 60,
        delay: i * 40
      });
    }
    
    setFlyingStars(newStars);
    setTimeout(() => setFlyingStars([]), 1500);
  };

  // Buy hint and reveal hotspot location
  const handleBuyHint = useCallback(async (hotspot) => {
    if (!user) {
      toast.info(t('toast.loginRequired') || 'Войдите, чтобы использовать подсказку');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/hotspots/${hotspot.id}/buy-hint`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.success) {
        setUserPointsBalance(response.data.new_balance);
        setHintDialog({ open: false, hotspot: null });
        setHighlightedHotspotId(hotspot.id);
        
        toast.success(
          t('toast.hintPurchased') || `Подсказка куплена! Списано ${response.data.points_spent} баллов`,
          { duration: 3000 }
        );
        
        // Center panorama on the hotspot and highlight it via postMessage to iframe
        const hs = response.data.hotspot;
        const iframe = document.querySelector('.krpano-viewer iframe');
        if (hs && iframe) {
          // Send message to iframe to look at hotspot and highlight it
          iframe.contentWindow?.postMessage({
            type: 'highlightHotspot',
            hotspotId: hs.id,
            ath: hs.ath,
            atv: hs.atv,
            points: hs.points
          }, '*');
          
          // Remove highlight marker in UI after 5 seconds
          setTimeout(() => {
            setHighlightedHotspotId(null);
          }, 5000);
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Ошибка при покупке подсказки';
      toast.error(errorMsg);
      setHintDialog({ open: false, hotspot: null });
    }
  }, [user, getAuthHeader, t]);

  // Handle hotspot click
  const handleHotspotClick = useCallback(async (hotspot) => {
    // Block hotspot interaction when viewing another user's results
    if (viewUserId) {
      return;
    }
    
    if (!user) {
      toast.info(t('toast.loginToGetPoints') || 'Войдите, чтобы получать баллы');
      return;
    }
    
    if (foundHotspotIds.includes(hotspot.id)) {
      setHotspotMessage({
        name: getTranslatedText(hotspot.name_translations, language, hotspot.name),
        points: 0,
        alreadyFound: true
      });
      setTimeout(() => setHotspotMessage(null), 2000);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/panoramas/${panoramaId}/hotspots/${hotspot.id}/found`,
        {},
        { headers: getAuthHeader() }
      );
      
      setFoundHotspotIds(prev => [...prev, hotspot.id]);
      // Also update game-wide counter
      setAllFoundHotspotIds(prev => {
        if (prev.includes(hotspot.id)) return prev;
        return [...prev, hotspot.id];
      });
      // Dispatch event to notify PageHeader about progress update
      window.dispatchEvent(new CustomEvent("hotspot-found", { detail: { hotspotId: hotspot.id } }));
      setHotspotMessage({
        name: getTranslatedText(response.data.hotspot_name_translations, language, response.data.hotspot_name),
        points: response.data.points_awarded,
        alreadyFound: response.data.already_found
      });
      
      // Trigger flying stars animation and update points immediately
      if (response.data.points_awarded > 0 && !response.data.already_found) {
        triggerFlyingStars(response.data.points_awarded);
        setUserPoints(prev => prev + response.data.points_awarded);
      }
      
      setTimeout(() => setHotspotMessage(null), 2000);
    } catch (error) {
      console.error('Error marking hotspot found:', error);
      toast.error(t('common.error') || 'Ошибка');
    }
  }, [user, foundHotspotIds, panoramaId, getAuthHeader, language, t, viewUserId]);
  
  // Listen for hotspot clicks from KRPano iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data && (event.data.type === 'hotspot_click' || event.data.type === 'hotspot_clicked')) {
        const hotspotData = event.data.hotspot || event.data;
        const hotspot_id = hotspotData.id || hotspotData.hotspot_id;
        const hotspot_name = hotspotData.name || hotspotData.hotspot_name;
        const reward_points = hotspotData.reward_points;
        
        // Track mouse position for stars animation
        const clickX = event.data.clickX;
        const clickY = event.data.clickY;
        if (clickX > 0 && clickY > 0) {
          lastMousePos.current = { x: clickX, y: clickY };
        } else if (viewerRef.current) {
          const rect = viewerRef.current.getBoundingClientRect();
          lastMousePos.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
        }
        
        const hs = hotspots.find(h => h.id === hotspot_id);
        if (hs) {
          handleHotspotClick(hs);
        } else if (hotspot_id) {
          handleHotspotClick({ id: hotspot_id, name: hotspot_name, reward_points });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [hotspots, foundHotspotIds, user, handleHotspotClick]);

  // Загрузить панораму после того как компонент отрендерился
  useEffect(() => {
    if (panorama && !loading && viewerRef.current) {
      loadKrpano(panorama);
    }
  }, [panorama, loading]);

  const fetchPanorama = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/panoramas/${panoramaId}`);
      setPanorama(response.data);
      // НЕ вызываем loadKrpano здесь - будет вызван в useEffect выше
    } catch (error) {
      console.error("Error fetching panorama:", error);
      toast.error("Ошибка загрузки панорамы");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const loadKrpano = (pano) => {
    if (!viewerRef.current) {
      console.error('viewerRef.current is null when trying to load panorama');
      return;
    }

    // Clear any existing content
    viewerRef.current.innerHTML = '';

    // Check if it's HTML-based panorama (Panotour) or krpano
    if (pano.html_file) {
      // Load HTML-based panorama via iframe
      const iframe = document.createElement("iframe");
      const iframeSrc = `${API}/panoramas/${pano.id}/files/${pano.html_file}`;
      iframe.src = iframeSrc;
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.style.border = "none";
      iframe.setAttribute("allowfullscreen", "true");
      iframe.setAttribute("allow", "accelerometer; gyroscope; vr");
      
      // Disable right-click context menu
      iframe.oncontextmenu = (e) => {
        e.preventDefault();
        return false;
      };
      
      viewerRef.current.appendChild(iframe);
    } else {
      // Load krpano viewer
      // Build correct path using base_dir
      const basePath = pano.base_dir ? `${pano.base_dir}/` : '';
      const script = document.createElement("script");
      script.src = `${API}/panoramas/${pano.id}/files/${basePath}krpano.js`;
      script.onload = () => {
        if (window.embedpano && viewerRef.current) {
          window.embedpano({
            xml: `${API}/panoramas/${pano.id}/files/${basePath}tour.xml`,
            target: viewerRef.current,
            html5: "prefer",
            mobilescale: 1.0,
            passQueryParameters: true,
          });
        }
      };
      script.onerror = () => {
        toast.error("Ошибка загрузки krpano viewer");
      };
      document.body.appendChild(script);
    }
  };

  return (
    <div className="viewer-container" data-testid="panorama-viewer" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {loading ? (
        <div className="loading-state">
          <Loader2 className="animate-spin" size={40} />
          <p>Загрузка панорамы...</p>
        </div>
      ) : (
        <div
          ref={viewerRef}
          className="krpano-viewer"
          data-testid="krpano-container"
          style={{ width: '100%', height: '100%' }}
        ></div>
      )}
      

      
      {/* Flying stars animation */}
      {flyingStars.map(star => {
        let targetX = window.innerWidth - 80;
        let targetY = 40;
        if (progressRef.current) {
          const rect = progressRef.current.getBoundingClientRect();
          targetX = rect.left + rect.width / 2;
          targetY = rect.top + rect.height / 2;
        }
        
        const keyframeName = `fly-viewer-${star.id}`;
        
        return (
          <div key={star.id}>
            <style>{`
              @keyframes ${keyframeName} {
                0% {
                  opacity: 1;
                  transform: translate(${star.startX}px, ${star.startY}px) scale(1.3);
                }
                80% {
                  opacity: 1;
                }
                100% {
                  opacity: 0;
                  transform: translate(${targetX}px, ${targetY}px) scale(0.4);
                }
              }
            `}</style>
            <div
              style={{
                position: 'fixed',
                left: 0,
                top: 0,
                fontSize: '24px',
                zIndex: 10000,
                pointerEvents: 'none',
                animation: `${keyframeName} 0.7s ease-in ${star.delay}ms forwards`
              }}
            >
              ⭐
            </div>
          </div>
        );
      })}
      
      {/* Hotspot found message */}
      {hotspotMessage && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: hotspotMessage.alreadyFound 
            ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
            : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          color: 'white',
          padding: '1.5rem 2.5rem',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          zIndex: 100003,
          textAlign: 'center',
          animation: 'bounceIn 0.5s ease-out'
        }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
            {hotspotMessage.alreadyFound ? '🔄' : '🎯'}
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.25rem' }}>
            {hotspotMessage.name}
          </div>
          <div style={{ fontSize: '14px',
            lineHeight: 1, opacity: 0.9 }}>
            {hotspotMessage.alreadyFound 
              ? (t('guessGame.alreadyFound') || 'Уже найдено!')
              : `+${hotspotMessage.points} ${t('guessGame.points') || 'баллов'}`
            }
          </div>
        </div>
      )}
      
      {/* Hotspots list button - top left, aligned with other badges */}
      {hotspots.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '1rem',
          zIndex: 1002
        }}>
          <div
            onClick={() => setShowHotspotsList(!showHotspotsList)}
            style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '1px solid #f59e0b',
              borderRadius: '16px',
              padding: '3px 8px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: '600',
              fontSize: '0.75rem',
              color: '#92400e',
              cursor: 'pointer'
            }}
            title={t("guessGame.whatToFind") || "Что искать"}
          >
            <Search size={12} />
            <span>{foundHotspotIds.length}/{hotspots.length}</span>
          </div>
          
          {showHotspotsList && (
            <div style={{
              position: 'fixed',
              top: '70px',
              left: '1rem',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              minWidth: '240px',
              maxWidth: 'calc(100vw - 2rem)',
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto',
              zIndex: 100002
            }}>
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e5e7eb',
                fontWeight: 600,
                color: '#374151',
                fontSize: '0.95rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{t("guessGame.findOnPanorama") || "Найдите на панораме"}</span>
                <X 
                  size={18} 
                  style={{ cursor: 'pointer', color: '#6b7280' }}
                  onClick={() => setShowHotspotsList(false)}
                />
              </div>
              {hotspots.map((hs, idx) => {
                const isFound = foundHotspotIds.includes(hs.id);
                const isHighlighted = highlightedHotspotId === hs.id;
                return (
                  <div
                    key={hs.id || idx}
                    style={{
                      padding: '10px 16px',
                      borderBottom: idx < hotspots.length - 1 ? '1px solid #f3f4f6' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      color: isFound ? '#22c55e' : isHighlighted ? '#f59e0b' : '#374151',
                      textDecoration: isFound ? 'line-through' : 'none',
                      opacity: isFound ? 0.7 : 1,
                      fontSize: '0.95rem',
                      backgroundColor: isHighlighted ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                      transition: 'background-color 0.3s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <span>{isFound ? '✓' : isHighlighted ? '👁' : '○'}</span>
                      <span>{getTranslatedText(hs.name_translations, language, hs.name) || `Объект ${idx + 1}`}</span>
                    </div>
                    {!isFound && !viewUserId && user && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setHintDialog({ open: true, hotspot: hs });
                        }}
                        style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          whiteSpace: 'nowrap'
                        }}
                        title="Показать объект за 100 баллов"
                      >
                        <Eye size={12} />
                        <span>100</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Hint purchase confirmation dialog */}
      {hintDialog.open && hintDialog.hotspot && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 300000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '360px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Eye size={28} color="white" />
            </div>
            
            <h3 style={{ 
              margin: '0 0 8px', 
              color: '#1f2937',
              fontSize: '1.25rem',
              fontWeight: 600
            }}>
              {t('hint.showObject') || 'Показать объект?'}
            </h3>
            
            <p style={{ 
              margin: '0 0 8px', 
              color: '#6b7280',
              fontSize: '0.95rem'
            }}>
              <strong style={{ color: '#374151' }}>
                {getTranslatedText(hintDialog.hotspot.name_translations, language, hintDialog.hotspot.name)}
              </strong>
            </p>
            
            <p style={{ 
              margin: '0 0 20px', 
              color: '#6b7280',
              fontSize: '0.9rem'
            }}>
              {t('hint.costInfo') || 'Стоимость подсказки:'} <strong style={{ color: '#f59e0b' }}>100 баллов</strong>
            </p>
            
            <p style={{ 
              margin: '0 0 20px', 
              color: '#9ca3af',
              fontSize: '0.85rem'
            }}>
              {t('hint.yourBalance') || 'Ваш баланс:'} <strong style={{ color: userPointsBalance >= 100 ? '#22c55e' : '#ef4444' }}>{Math.round(userPointsBalance)} баллов</strong>
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setHintDialog({ open: false, hotspot: null })}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#6b7280',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                {t('common.cancel') || 'Отмена'}
              </button>
              <button
                onClick={() => handleBuyHint(hintDialog.hotspot)}
                disabled={userPointsBalance < 100}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  background: userPointsBalance >= 100 
                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                    : '#e5e7eb',
                  color: userPointsBalance >= 100 ? 'white' : '#9ca3af',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  cursor: userPointsBalance >= 100 ? 'pointer' : 'not-allowed'
                }}
              >
                {t('hint.showButton') || 'Показать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Back button at bottom left */}
      <Button
        data-testid="back-btn"
        onClick={() => navigate(panorama?.game_id ? `/${panorama.game_id}` : '/')}
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          zIndex: 1002,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.25rem',
          background: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '0.95rem',
          fontWeight: 500,
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
        }}
        title={panorama?.game_id ? "Вернуться к игре" : "Вернуться к списку"}
      >
        <ArrowLeft size={18} />
        {panorama?.game_id ? "Назад" : "На главную"}
      </Button>
    </div>
  );
};

export default PanoramaViewer;